/**
 * Wraps every /platform/* route (Societies, Leads, Create, Audit log, Team)
 * with PlatformGate, then lays a compact console sidebar beside the routed
 * screen on wide web — mirrors components/shell/DesktopShell.tsx's SideLink
 * styling so the console reads as one family with the resident shell without
 * sharing its nav items. Create + Audit log + Team are SUPER_ADMIN only
 * (capability matrix: create-society is a SUPER_ADMIN-only capability, same
 * tier as audit/team — a SALES/SUPPORT/MARKETING staffer must never see a
 * nav link that 403s). Below WIDE
 * (or native, which no Bero staffer actually uses this from) the links
 * become a horizontal scroller above the content instead of a side column.
 */
import { router, Slot, usePathname } from 'expo-router';
import React from 'react';
import { Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { space } from '@bero/tokens';
import { ArchAvatar } from '@/features/community/bits';
import { Icon } from '@/features/account-admin/icons';
import { PlatformGate, usePlatformRole } from '@/features/platform/PlatformGate';
import { useSession } from '@/lib/session';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Hairline, TopBar } from '@/ui';

const WIDE = 1024;

const LINKS: readonly (readonly [DictKey, string])[] = [
  ['plat_nav_societies', '/platform'],
  ['plat_nav_leads', '/platform/leads'],
];

const SUPER_ADMIN_LINKS: readonly (readonly [DictKey, string])[] = [
  ['plat_nav_create', '/platform/create'],
  ['plat_nav_audit', '/platform/audit'],
  ['plat_nav_members', '/platform/members'],
];

/** Same humanised labels the Team screen and the acting banner use. */
const PLATFORM_ROLE_LABEL: Record<string, DictKey> = {
  SUPER_ADMIN: 'plat_role_super_admin',
  SALES: 'plat_role_sales',
  SUPPORT: 'plat_role_support',
  MARKETING: 'plat_role_marketing',
};

const TITLES: Record<string, DictKey> = {
  '/platform': 'plat_nav_societies',
  '/platform/leads': 'plat_leads_title',
  '/platform/create': 'plat_create_title',
  '/platform/audit': 'plat_audit_title',
  '/platform/members': 'plat_members_title',
};

function linksFor(role: string) {
  return role === 'SUPER_ADMIN' ? [...LINKS, ...SUPER_ADMIN_LINKS] : LINKS;
}

function SideLink({ label, path, horizontal }: { label: string; path: string; horizontal?: boolean }) {
  const { colors } = useTheme();
  const pathname = usePathname();
  const active = pathname === path || (path !== '/platform' && pathname.startsWith(path));
  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => router.push(path as never)}
      style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
        paddingVertical: 9, paddingHorizontal: space.s3, borderRadius: 8,
        backgroundColor: active ? colors.primary100 : hovered || pressed ? colors.canvas1 : 'transparent',
        ...(horizontal ? {} : { marginBottom: 2 }),
      })}
    >
      <AppText variant="bodyMd" weight={active ? '600' : '400'}
        style={{ color: active ? colors.primary900 : colors.textSecondary }}>
        {label}
      </AppText>
    </Pressable>
  );
}

/** Who am I, what am I, get me out — the console had none of these (reported
 * 2026-07-22: "where is the logout button"). Mirrors the resident sidebar's
 * footer (DesktopShell.tsx): avatar + identity, theme toggle, sign out. */
function AccountFooter({ compact }: { compact?: boolean }) {
  const { colors, scheme, toggle } = useTheme();
  const { t } = useT();
  const { user, signOut } = useSession();
  const role = usePlatformRole();
  const [signingOut, setSigningOut] = React.useState(false);
  const roleLabel = PLATFORM_ROLE_LABEL[role] ? t(PLATFORM_ROLE_LABEL[role]) : role;

  const logout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
      router.replace('/login');
    } finally {
      setSigningOut(false);
    }
  };

  // Identity above, controls below: at 232px a single row squeezed the name to
  // "Ami…" and the role to "Sup…", and Bero never truncates labels.
  const controls = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={scheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        onPress={toggle}
        style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
          width: 34, height: 34, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
          borderWidth: 1, borderColor: colors.borderSoft,
          backgroundColor: hovered || pressed ? colors.canvas1 : 'transparent',
        })}
      >
        <Icon name={scheme === 'dark' ? 'sun' : 'moon'} size={16} color={colors.textSecondary} />
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('sign_out')}
        disabled={signingOut}
        onPress={() => void logout()}
        style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
          flex: 1, alignItems: 'center',
          paddingHorizontal: space.s3, paddingVertical: 8, borderRadius: 8,
          borderWidth: 1, borderColor: colors.borderFirm, opacity: signingOut ? 0.5 : 1,
          backgroundColor: hovered || pressed ? colors.danger50 : 'transparent',
        })}
      >
        <AppText variant="bodySm" weight="600" style={{ color: colors.danger500 }}>{t('sign_out')}</AppText>
      </Pressable>
    </View>
  );

  const identity = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, flex: compact ? 1 : undefined, minWidth: 0 }}>
      <ArchAvatar name={user?.fullName ?? 'Bero'} size={34} tone="primary" />
      <View style={{ flex: 1, minWidth: 0 }}>
        <AppText variant="bodySm" weight="600">{user?.fullName ?? t('nav_signed_in')}</AppText>
        <AppText variant="bodySm" tone="tertiary">{roleLabel}</AppText>
      </View>
    </View>
  );

  // Narrow/mobile web has the full page width to play with, so identity and
  // controls fit on one line there; the 232px sidebar stacks them.
  return compact ? (
    <View style={{ paddingHorizontal: space.s3, flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
      {identity}
      {controls}
    </View>
  ) : (
    <View style={{ padding: space.s3, gap: space.s3 }}>
      {identity}
      {controls}
    </View>
  );
}

function PlatformShell({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const { t } = useT();
  const role = usePlatformRole();
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const links = linksFor(role);
  // Screens under /platform/society/ render their own TopBar (title + back).
  const ownTopBar = pathname.startsWith('/platform/society');
  // The TopBar carries each screen's own page title (richer than the nav label,
  // e.g. "Leads & prospects" vs "Leads") — the screens no longer print it
  // themselves, so it must live somewhere.
  const activeLabel = t(TITLES[Object.keys(TITLES).filter((p) => p !== '/platform' && pathname.startsWith(p))[0]
    ?? '/platform']);

  if (!wide) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
        <View style={{ paddingHorizontal: space.s2, paddingTop: space.s4 }}>
          <AccountFooter compact />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          // flexGrow:0 + centered items, or the scroller claims the leftover
          // column height and stretches the active link's pill down the page.
          style={{ flexGrow: 0, flexShrink: 0 }}
          contentContainerStyle={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, paddingHorizontal: space.s4, paddingVertical: space.s3 }}
        >
          {links.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} horizontal />)}
        </ScrollView>
        <Hairline />
        <View style={{ flex: 1 }}>{children}</View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
      <View style={{ width: 232, borderRightWidth: 0.5, borderRightColor: colors.borderSoft, backgroundColor: colors.canvas0 }}>
        <View style={{ paddingHorizontal: space.s4, height: 56, justifyContent: 'center' }}>
          <BrandLockup markHeight={22} />
        </View>
        <Hairline />
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: space.s3, gap: 2 }} showsVerticalScrollIndicator={false}>
          <AppText variant="label" tone="tertiary" style={{ paddingHorizontal: space.s3, marginBottom: 6 }}>
            {t('plat_title')}
          </AppText>
          {links.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} />)}
        </ScrollView>
        <Hairline />
        <AccountFooter />
      </View>
      <View style={{ flex: 1 }}>
        {/* The sidebar's lockup band ends in a hairline at 56px; the content pane
            needs the SAME band or that line dead-ends at the sidebar edge and
            visually slices through whatever sits at that height (the audit
            filter chips, reported 2026-07-22). TopBar gives both panes an
            identical 56px + hairline. Detail routes (society/[id]) bring their
            own TopBar with a back button, so they opt out here. */}
        {ownTopBar ? null : <TopBar title={activeLabel} brand="none" />}
        {children}
      </View>
    </View>
  );
}

export default function PlatformLayout() {
  return (
    <PlatformGate>
      <PlatformShell>
        <Slot />
      </PlatformShell>
    </PlatformGate>
  );
}
